// src/vfx/background.js
// ─────────────────────────────────────────────────────────────────────────────
// Ethereal Battle-Field background — seven layered systems, all driven by
// RegionThemes so every region is visually distinct automatically.
//
// Depth budget (board tiles use Phaser default depth 0, added AFTER this runs,
// so same-depth background objects are drawn first / behind tiles):
//
//   0   gradient fill
//   0   nebula wisps           ← same depth, behind tiles (added first)
//   0   star field             ← same depth, behind tiles (added first)
//   0   edge vignette          ← same depth, behind tiles (added first)
//   0   bottom glow orb        ← same depth, behind tiles (added first)
//   0   board portal           ← same depth, behind tiles (added first)
//  10   ascending particles    ← in FRONT of board tiles (atmospheric)
//  25   board frame + corners  ← in FRONT of tiles (frame encloses board)
//  25   energy floor line
//
//  MonsterBar: 50-56   HUD: 1000+   Modals: 1000+
// ─────────────────────────────────────────────────────────────────────────────

import { RegionThemes, hexToPhaser } from "../data/regionThemes.js";

// ── Per-region ambient particle configs (used by _startParticles) ─────────────
const PARTICLE_CFG = {
  embers:  { count: 12, minDur: 2500, maxDur: 4500, drift: 18, interval: 380, sizeMin: 1.5, sizeMax: 3.5 },
  bubbles: { count: 10, minDur: 3000, maxDur: 5500, drift: 10, interval: 500, sizeMin: 2.0, sizeMax: 4.0 },
  leaves:  { count:  8, minDur: 4000, maxDur: 7000, drift: 45, interval: 700, sizeMin: 2.0, sizeMax: 5.0 },
  sparks:  { count: 16, minDur:  800, maxDur: 2000, drift: 35, interval: 150, sizeMin: 1.0, sizeMax: 2.5 },
  petals:  { count:  8, minDur: 5000, maxDur: 8000, drift: 30, interval: 650, sizeMin: 2.0, sizeMax: 4.0 },
};

// ── Public entry point ────────────────────────────────────────────────────────
export function createBackground(scene, regionId, opts = {}) {
  const theme = RegionThemes[regionId] || RegionThemes.volcano;
  const W     = scene.scale.width;
  const H     = scene.scale.height;

  _drawGradient(scene, theme, W, H);
  _drawNebula(scene, theme, W, H);
  _drawStars(scene, theme, W, H);
  _drawVignette(scene, W, H);
  _drawGlowOrb(scene, theme, W, H);
  _startParticles(scene, theme, W, H);

  let boardFrame = null;
  if (opts.boardX !== undefined) {
    _drawBoardPortal(scene, theme, opts.boardX, opts.boardY, opts.boardW, opts.boardH);
    boardFrame = _drawBoardFrame(scene, theme, opts.boardX, opts.boardY, opts.boardW, opts.boardH);
    _drawFloorLine(scene, theme, opts.boardX, opts.boardY, opts.boardW, opts.boardH);
  }

  const boardCX = (opts.boardX ?? W / 2) + (opts.boardW ?? 0) / 2;
  const boardCY = (opts.boardY ?? H / 2) + (opts.boardH ?? 0) / 2;
  const { triggerBurst, updateFn: particleUpdate } = _startRegionParticles(scene, regionId, theme, boardCX, boardCY, W, H);
  const musicUpdate = _startBorderEqualizer(scene, opts.analyserGetter, theme, opts.boardX, opts.boardY, opts.boardW, opts.boardH);
  const bgUpdate = () => { particleUpdate?.(); musicUpdate?.(); };
  return { boardFrame, triggerBurst, bgUpdate };
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Gradient fill — dark, tinted by region colours
// ─────────────────────────────────────────────────────────────────────────────
function _drawGradient(scene, theme, W, H) {
  const top = hexToPhaser(theme.bgTop);
  const bot = hexToPhaser(theme.bgBottom);
  const g   = scene.add.graphics().setDepth(0);
  g.fillGradientStyle(top, top, bot, bot, 0.28);
  g.fillRect(0, 0, W, H);
}

// ─────────────────────────────────────────────────────────────────────────────
// 1b. Region background image — painted art behind the VFX layers
//     Alpha ~0.38 so the dark gradient still sets the mood and nebula/stars
//     layer naturally on top. Slow breathe tween adds life.
// ─────────────────────────────────────────────────────────────────────────────
function _drawBgImage(scene, theme, W, H) {
  const img = scene.add.image(W / 2, H / 2, theme.bgKey)
    .setDisplaySize(W, H)
    .setAlpha(0.82)
    .setDepth(0);

  scene.tweens.add({
    targets:  img,
    alpha:    { from: 0.82, to: 0.72 },
    duration: 6000,
    yoyo:     true,
    repeat:   -1,
    ease:     'Sine.InOut',
  });

  // ── Region colour bloom — ADD blend illuminates matching hues in the art ──
  const bloomColor = hexToPhaser(theme.primary);
  const bloom = scene.add
    .rectangle(W / 2, H / 2, W, H, bloomColor, 1)
    .setBlendMode(Phaser.BlendModes.ADD)
    .setAlpha(0.10)
    .setDepth(0);

  scene.tweens.add({
    targets:  bloom,
    alpha:    { from: 0.10, to: 0.18 },
    duration: 4000,
    yoyo:     true,
    repeat:   -1,
    ease:     'Sine.InOut',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Nebula wisps — soft large blobs that breathe slowly
// ─────────────────────────────────────────────────────────────────────────────
function _drawNebula(scene, theme, W, H) {
  const primary = hexToPhaser(theme.primary);
  const glow    = hexToPhaser(theme.glow);

  // Blob layout: deliberately asymmetric for organic feel
  const blobs = [
    { x: W * 0.12, y: H * 0.20, r: 140, color: glow,    alpha: 0.14, delay: 0    },
    { x: W * 0.88, y: H * 0.16, r: 105, color: primary,  alpha: 0.09, delay: 1400 },
    { x: W * 0.75, y: H * 0.40, r: 170, color: glow,    alpha: 0.11, delay: 2800 },
    { x: W * 0.20, y: H * 0.55, r: 115, color: primary,  alpha: 0.08, delay: 800  },
    { x: W * 0.50, y: H * 0.76, r: 150, color: glow,    alpha: 0.13, delay: 2100 },
  ];

  for (const b of blobs) {
    const g = scene.add.graphics().setDepth(0);

    // Outer diffuse halo
    g.fillStyle(b.color, b.alpha);
    g.fillCircle(b.x, b.y, b.r);

    // Brighter inner core — creates the "nebula density" illusion
    g.fillStyle(b.color, b.alpha * 1.9);
    g.fillCircle(b.x, b.y, b.r * 0.42);

    scene.tweens.add({
      targets:  g,
      alpha:    { from: 1, to: Phaser.Math.FloatBetween(0.35, 0.70) },
      duration: Phaser.Math.Between(4500, 8000),
      yoyo:     true,
      repeat:   -1,
      ease:     'Sine.InOut',
      delay:    b.delay,
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Star field — three parallax layers with staggered twinkles
//    15% of stars use the region primary colour for subtle magic sparkle
// ─────────────────────────────────────────────────────────────────────────────
function _drawStars(scene, theme, W, H) {
  const starColor    = hexToPhaser(theme.starColor);
  const primaryColor = hexToPhaser(theme.primary);

  const layers = [
    { count:  6, size: 3.5, depth: 0, minMs: 2800, maxMs: 5000 },
    { count: 14, size: 2.0, depth: 0, minMs: 1800, maxMs: 3500 },
    { count: 28, size: 1.0, depth: 0, minMs: 1000, maxMs: 2200 },
  ];

  for (const cfg of layers) {
    for (let i = 0; i < cfg.count; i++) {
      const x          = Phaser.Math.Between(0, W);
      const y          = Phaser.Math.Between(0, H * 0.82); // avoid monster bar area
      const isPrimary  = Math.random() > 0.85;
      const baseAlpha  = Phaser.Math.FloatBetween(0.45, 0.95);

      const star = scene.add
        .rectangle(x, y, cfg.size, cfg.size, isPrimary ? primaryColor : starColor, 1)
        .setAlpha(baseAlpha)
        .setDepth(cfg.depth);

      scene.tweens.add({
        targets:  star,
        alpha:    { from: baseAlpha, to: Phaser.Math.FloatBetween(0.04, 0.20) },
        duration: Phaser.Math.Between(cfg.minMs, cfg.maxMs),
        yoyo:     true,
        repeat:   -1,
        delay:    Phaser.Math.Between(0, 3500),
        ease:     'Sine.InOut',
      });
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Edge vignette — draws the eye toward the board, cinematic framing
// ─────────────────────────────────────────────────────────────────────────────
function _drawVignette(scene, W, H) {
  // Top edge
  scene.add.rectangle(0,      0,       W,   70, 0x000000, 0.18).setOrigin(0, 0).setDepth(0);
  // Bottom edge
  scene.add.rectangle(0,      H - 55,  W,   55, 0x000000, 0.15).setOrigin(0, 0).setDepth(0);
  // Left edge
  scene.add.rectangle(0,      0,       32,  H,  0x000000, 0.12).setOrigin(0, 0).setDepth(0);
  // Right edge
  scene.add.rectangle(W - 32, 0,       32,  H,  0x000000, 0.12).setOrigin(0, 0).setDepth(0);
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Bottom glow orb — atmospheric energy source rising from below
//    Four concentric circles + breathe tween
// ─────────────────────────────────────────────────────────────────────────────
function _drawGlowOrb(scene, theme, W, H) {
  const glowColor    = hexToPhaser(theme.glow);
  const primaryColor = hexToPhaser(theme.primary);

  // Orb is centred well WITHIN the screen so the halo is visible
  const orbY = H - 90;

  const orb = scene.add.graphics().setDepth(0);

  // Outermost diffuse halo (wide, barely visible)
  orb.fillStyle(glowColor, 0.07);
  orb.fillCircle(W / 2, orbY, 310);

  // Mid halo
  orb.fillStyle(glowColor, 0.14);
  orb.fillCircle(W / 2, orbY, 195);

  // Inner glow (primary colour bleeds in)
  orb.fillStyle(primaryColor, 0.12);
  orb.fillCircle(W / 2, orbY, 110);

  // Bright core
  orb.fillStyle(primaryColor, 0.18);
  orb.fillCircle(W / 2, orbY, 55);

  // Breathe in and out
  scene.tweens.add({
    targets:  orb,
    alpha:    { from: 1.0, to: 0.48 },
    duration: 3200,
    yoyo:     true,
    repeat:   -1,
    ease:     'Sine.InOut',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Ascending particles — tiny glowing motes rising from the lower screen
//    Spawn via a recurring timer so the scene stays alive indefinitely
// ─────────────────────────────────────────────────────────────────────────────
function _startParticles(scene, theme, W, H) {
  const cfg          = PARTICLE_CFG[theme.particle] || PARTICLE_CFG.embers;
  const primaryColor = hexToPhaser(theme.primary);
  const secondColor  = hexToPhaser(theme.secondary);

  const spawnOne = () => {
    const usePrimary = Math.random() > 0.55;
    const color      = usePrimary ? primaryColor : secondColor;
    const size       = Phaser.Math.FloatBetween(cfg.sizeMin, cfg.sizeMax);
    const x          = Phaser.Math.Between(24, W - 24);
    const startY     = Phaser.Math.Between(H * 0.58, H * 0.88);
    const rise       = Phaser.Math.Between(220, 480);
    const duration   = Phaser.Math.Between(cfg.minDur, cfg.maxDur);
    const driftX     = Phaser.Math.Between(-cfg.drift, cfg.drift);
    const startAlpha = Phaser.Math.FloatBetween(0.55, 0.95);

    const p = scene.add
      .rectangle(x, startY, size, size, color, startAlpha)
      .setDepth(10); // in front of board tiles (depth 0)

    scene.tweens.add({
      targets:  p,
      y:        startY - rise,
      x:        x + driftX,
      alpha:    0,
      duration,
      ease:     'Quad.In',
      onComplete: () => p.destroy(),
    });
  };

  // Stagger initial burst so the first frame isn't empty
  for (let i = 0; i < cfg.count; i++) {
    scene.time.delayedCall(i * cfg.interval, spawnOne);
  }

  // Ongoing continuous spawn
  scene.time.addEvent({ delay: cfg.interval, callback: spawnOne, loop: true });
}

// ─────────────────────────────────────────────────────────────────────────────
// 7a. Board portal glow — soft luminous aura behind the tile grid
// ─────────────────────────────────────────────────────────────────────────────
function _drawBoardPortal(scene, theme, boardX, boardY, boardW, boardH) {
  const primaryColor = hexToPhaser(theme.primary);
  const glowColor    = hexToPhaser(theme.glow);

  const g = scene.add.graphics().setDepth(0);

  // Dark overlay behind tiles — keeps gems readable against vivid backgrounds
  g.fillStyle(0x000000, 0.42);
  g.fillRect(boardX, boardY, boardW, boardH);

  // Wide outer aura
  g.fillStyle(glowColor, 0.09);
  g.fillRect(boardX - 22, boardY - 22, boardW + 44, boardH + 44);

  // Tighter mid glow
  g.fillStyle(primaryColor, 0.06);
  g.fillRect(boardX - 10, boardY - 10, boardW + 20, boardH + 20);

  // Direct tile-area tint (very subtle — just enough to warm the dark tiles)
  g.fillStyle(primaryColor, 0.04);
  g.fillRect(boardX, boardY, boardW, boardH);

  // Gentle pulse
  scene.tweens.add({
    targets:  g,
    alpha:    { from: 1, to: 0.45 },
    duration: 2600,
    yoyo:     true,
    repeat:   -1,
    ease:     'Sine.InOut',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 7b. Board frame — triple-weight border + bright corner brackets
//     Sits IN FRONT of tile graphics (depth 25)
// ─────────────────────────────────────────────────────────────────────────────
function _drawBoardFrame(scene, theme, boardX, boardY, boardW, boardH) {
  const accent = hexToPhaser(theme.boardGlow);

  const frame = scene.add.graphics().setDepth(25);

  // Outer diffuse glow stroke
  frame.lineStyle(9, accent, 0.07);
  frame.strokeRect(boardX - 7, boardY - 7, boardW + 14, boardH + 14);

  // Medium glow stroke
  frame.lineStyle(4, accent, 0.22);
  frame.strokeRect(boardX - 3, boardY - 3, boardW + 6,  boardH + 6);

  // Crisp inner border
  frame.lineStyle(1, accent, 0.55);
  frame.strokeRect(boardX - 1, boardY - 1, boardW + 2,  boardH + 2);

  // Corner bracket accent lines — the "cyberpunk HUD portal" signature
  const L  = 26;           // arm length
  const lx = boardX - 3;
  const ty = boardY - 3;
  const rx = boardX + boardW + 3;
  const by = boardY + boardH + 3;

  frame.lineStyle(3, accent, 1.0);

  // ┌ top-left
  frame.beginPath();
  frame.moveTo(lx,     ty + L);
  frame.lineTo(lx,     ty);
  frame.lineTo(lx + L, ty);
  frame.strokePath();

  // ┐ top-right
  frame.beginPath();
  frame.moveTo(rx - L, ty);
  frame.lineTo(rx,     ty);
  frame.lineTo(rx,     ty + L);
  frame.strokePath();

  // └ bottom-left
  frame.beginPath();
  frame.moveTo(lx,     by - L);
  frame.lineTo(lx,     by);
  frame.lineTo(lx + L, by);
  frame.strokePath();

  // ┘ bottom-right
  frame.beginPath();
  frame.moveTo(rx - L, by);
  frame.lineTo(rx,     by);
  frame.lineTo(rx,     by - L);
  frame.strokePath();

  // Frame pulses slowly — the board feels "alive"
  scene.tweens.add({
    targets:  frame,
    alpha:    { from: 1.0, to: 0.55 },
    duration: 1900,
    yoyo:     true,
    repeat:   -1,
    ease:     'Sine.InOut',
    delay:    600,
  });

  return frame;
}

// ─────────────────────────────────────────────────────────────────────────────
// System 1 — Music-Reactive Border Equalizer
//   The board frame IS the equalizer. Bass drives the outer glow width/alpha.
//   8 sparkles (4 CW + 4 CCW) travel along the perimeter at energy-scaled speed.
//   Depth 26/27 — above the static board frame (25), below HUD (1000).
//   Safe for photosensitivity: no full-screen flash, all effects localised to frame.
// ─────────────────────────────────────────────────────────────────────────────
function _startBorderEqualizer(scene, analyserGetter, theme, boardX, boardY, boardW, boardH) {
  if (!analyserGetter || boardX === undefined) return null;

  const PRIMARY    = hexToPhaser(theme.primary);
  const SECONDARY  = hexToPhaser(theme.secondary);
  const N_SPARKLES = 14;                          // more sparkles = denser trail
  const perimeter  = 2 * (boardW + boardH);

  // t (0..perimeter) → {x, y} along the board rectangle border
  const perimPos = (t) => {
    t = ((t % perimeter) + perimeter) % perimeter;
    if (t < boardW)  return { x: boardX + t,           y: boardY           };
    t -= boardW;
    if (t < boardH)  return { x: boardX + boardW,      y: boardY + t       };
    t -= boardH;
    if (t < boardW)  return { x: boardX + boardW - t,  y: boardY + boardH  };
    t -= boardW;
    return                  { x: boardX,               y: boardY + boardH - t };
  };

  // One Graphics object redrawn each frame for the reactive glow stroke
  const glowG = scene.add.graphics().setDepth(26).setBlendMode(Phaser.BlendModes.ADD);

  // Sparkle pool — first half CW (dir+1), second half CCW (dir-1)
  const sparkles = Array.from({ length: N_SPARKLES }, (_, i) => {
    const offset = (i / N_SPARKLES) * perimeter;
    const pos    = perimPos(offset);
    const color  = i % 3 === 0 ? 0xffffff : (i % 3 === 1 ? PRIMARY : SECONDARY);
    const obj    = scene.add
      .rectangle(pos.x, pos.y, 5, 5, color, 1.0)
      .setDepth(27)
      .setBlendMode(Phaser.BlendModes.ADD);
    return { obj, offset, dir: i < N_SPARKLES / 2 ? 1 : -1 };
  });

  let dataArray    = null;
  let smoothEnergy = 0;

  return () => {
    const analyser = analyserGetter();
    if (!analyser) return;
    if (!dataArray) dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);

    // Bass = first ~15% of frequency bands (where the kick drum lives)
    const bassCount = Math.max(1, Math.floor(dataArray.length * 0.15));
    let bassSum = 0, totalSum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      totalSum += dataArray[i];
      if (i < bassCount) bassSum += dataArray[i];
    }
    const bassEnergy  = bassSum  / (bassCount        * 255);
    const totalEnergy = totalSum / (dataArray.length * 255);
    // Less smoothing = more reactive to individual beats
    smoothEnergy = smoothEnergy * 0.60 + totalEnergy * 0.40;

    // ── Three-layer reactive glow ──
    glowG.clear();
    // Outer diffuse halo — wide, soft
    glowG.lineStyle(14 + bassEnergy * 40, PRIMARY,   0.18 + bassEnergy * 0.55);
    glowG.strokeRect(boardX - 10, boardY - 10, boardW + 20, boardH + 20);
    // Mid ring — tighter, brighter
    glowG.lineStyle(5  + bassEnergy * 12, SECONDARY, 0.35 + bassEnergy * 0.45);
    glowG.strokeRect(boardX - 4,  boardY - 4,  boardW + 8,  boardH + 8);
    // Inner crisp white flash on beat
    glowG.lineStyle(2,             0xffffff,  0.20 + bassEnergy * 0.80);
    glowG.strokeRect(boardX,       boardY,    boardW,        boardH);

    // ── Move sparkles along the perimeter ──
    const spd   = 1.5 + smoothEnergy * 8.0;
    const alpha = 0.60 + smoothEnergy * 0.40;   // always visible, brightens with energy
    const size  = 4   + smoothEnergy * 8;
    for (const sp of sparkles) {
      sp.offset += sp.dir * spd;
      const pos = perimPos(sp.offset);
      sp.obj.setPosition(pos.x, pos.y).setAlpha(alpha).setSize(size, size);
    }
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// System 2 — Per-Region Gameplay Particles
//   Returns { triggerBurst(opts), updateFn } per region.
// ─────────────────────────────────────────────────────────────────────────────
function _startRegionParticles(scene, regionId, theme, boardCX, boardCY, W, H) {
  switch (regionId) {
    case 'volcano': return _startVolcano(scene, theme, W, H);
    case 'ocean':   return _startOcean(scene, theme, W, H);
    case 'storm':   return _startStorm(scene, theme, W, H);
    case 'earth':   return _startEarth(scene, theme, W, H);
    case 'psychic': return _startPsychic(scene, theme, boardCX, boardCY, W, H);
    default:        return { triggerBurst: () => {}, updateFn: null };
  }
}

// ── 🌋 Volcano — Tension builds → Eruption ───────────────────────────────────
function _startVolcano(scene, theme, W, H) {
  let tension      = 0;
  let lastEruption = 0;

  const spawnEmbers = (count, speedMult = 1.0) => {
    for (let i = 0; i < count; i++) {
      scene.time.delayedCall(i * 28, () => {
        const x     = Phaser.Math.Between(W * 0.05, W * 0.95);
        const size  = Phaser.Math.FloatBetween(3, 7);
        const t     = tension / 10;
        const color = t < 0.4 ? 0xff2200 : t < 0.7 ? 0xff6600 : 0xffcc44;
        const p     = scene.add.rectangle(x, H * 0.9, size, size, color, 1.0)
          .setDepth(8).setBlendMode(Phaser.BlendModes.ADD);
        const riseH = Phaser.Math.Between(180, 420) * speedMult;
        const dur   = Phaser.Math.Between(700, 1800) / speedMult;
        scene.tweens.add({
          targets: p, y: H * 0.9 - riseH,
          x: x + Phaser.Math.Between(-40, 40), alpha: 0, duration: dur,
          ease: 'Quad.In', onComplete: () => p.destroy(),
        });
      });
    }
  };

  // Ambient — starts at 6 embers, rate and count grow with tension
  const ambientTick = () => {
    spawnEmbers(6 + Math.floor(tension / 2));
    scene.time.delayedCall(Math.max(80, 350 - tension * 30), ambientTick);
  };
  ambientTick();

  // Auto-eruption if no big combo for 8s and tension > 0
  scene.time.addEvent({
    delay: 8000, loop: true,
    callback: () => {
      if (tension > 0 && scene.time.now - lastEruption > 8000) {
        _volcanicEruption(scene, W, H);
        tension = 0; lastEruption = scene.time.now;
      }
    },
  });

  const triggerBurst = ({ type = 'match', comboLevel = 1 } = {}) => {
    tension = Math.min(10, tension + 1);
    if (comboLevel >= 3 || type === 'big_combo') {
      _volcanicEruption(scene, W, H);
      tension = 0; lastEruption = scene.time.now;
    } else {
      spawnEmbers(3 + tension, 1.4);
    }
  };

  return { triggerBurst, updateFn: null };
}

function _volcanicEruption(scene, W, H) {
  for (let i = 0; i < 80; i++) {
    scene.time.delayedCall(i * 14, () => {
      const x     = Phaser.Math.Between(W * 0.05, W * 0.95);
      const size  = Phaser.Math.FloatBetween(4, 13);
      const color = [0xff2200, 0xff6600, 0xffaa00, 0xffdd44, 0xffffff][Math.floor(Math.random() * 5)];
      const p     = scene.add.rectangle(x, H, size, size, color, 1.0)
        .setDepth(8).setBlendMode(Phaser.BlendModes.ADD);
      scene.tweens.add({
        targets: p, y: Phaser.Math.Between(H * 0.05, H * 0.55),
        x: x + Phaser.Math.Between(-80, 80), alpha: 0,
        duration: Phaser.Math.Between(700, 1500), ease: 'Quad.Out',
        onComplete: () => p.destroy(),
      });
    });
  }
  // No full-screen flash — photosensitivity safety
}

// ── 🌊 Ocean — Wave Flow ─────────────────────────────────────────────────────
function _startOcean(scene, theme, W, H) {
  let waveDir   = 1;
  let amplitude = 30;

  const scheduleFlip = () => {
    scene.time.delayedCall(Phaser.Math.Between(6000, 10000), () => { waveDir *= -1; scheduleFlip(); });
  };
  scheduleFlip();

  const spawnParticle = (burst = false) => {
    const startX  = waveDir > 0 ? -12 : W + 12;
    const targetX = waveDir > 0 ? W + 12 : -12;
    const baseY   = Phaser.Math.Between(H * 0.1, H * 0.82);
    const size    = Phaser.Math.FloatBetween(2, 4.5);
    const color   = Math.random() > 0.5 ? hexToPhaser(theme.primary) : hexToPhaser(theme.secondary);
    const dur     = burst ? Phaser.Math.Between(1200, 2500) : Phaser.Math.Between(2800, 5500);
    const p       = scene.add.rectangle(startX, baseY, size, size, color, 0.75).setDepth(8);
    scene.tweens.add({
      targets: p, x: targetX, y: baseY + Phaser.Math.Between(-amplitude, amplitude),
      alpha: 0, duration: dur, ease: 'Sine.InOut', onComplete: () => p.destroy(),
    });
  };

  scene.time.addEvent({ delay: 220, loop: true, callback: () => spawnParticle() });

  const triggerBurst = ({ type = 'match', comboLevel = 1 } = {}) => {
    amplitude = Math.min(80, amplitude + 20);
    const count = comboLevel >= 3 ? 18 : 10;
    for (let i = 0; i < count; i++) scene.time.delayedCall(i * 55, () => spawnParticle(true));
    scene.time.delayedCall(2500, () => { amplitude = Math.max(30, amplitude - 20); });
  };

  return { triggerBurst, updateFn: null };
}

// ── ⚡ Storm — Electric Bolts ─────────────────────────────────────────────────
function _startStorm(scene, theme, W, H) {
  const primaryColor = hexToPhaser(theme.primary);

  const spawnBolt = () => {
    const boltX = Phaser.Math.Between(20, W - 20);
    const SEGS  = 10;
    const segH  = H / SEGS;
    const pts   = [{ x: boltX, y: H }];
    let curX    = boltX;
    for (let i = SEGS - 1; i >= 0; i--) {
      curX = Phaser.Math.Clamp(curX + Phaser.Math.Between(-16, 16), 10, W - 10);
      pts.push({ x: curX, y: i * segH });
    }

    const g = scene.add.graphics().setDepth(-1).setBlendMode(Phaser.BlendModes.ADD);
    g.lineStyle(3, primaryColor, 0.35);
    g.beginPath(); g.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
    g.strokePath();
    g.lineStyle(1, 0xffffff, 1.0);
    g.beginPath(); g.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
    g.strokePath();

    scene.tweens.add({
      targets: g, alpha: 0,
      duration: Phaser.Math.Between(120, 280), ease: 'Quad.In',
      onComplete: () => g.destroy(),
    });
  };

  // Ambient: 1-2 bolts every ~2s
  scene.time.addEvent({
    delay: 2000, loop: true,
    callback: () => {
      spawnBolt();
      if (Math.random() > 0.55) scene.time.delayedCall(350, spawnBolt);
    },
  });

  const triggerBurst = ({ type = 'match', comboLevel = 1 } = {}) => {
    const count = comboLevel >= 3 ? 5 : 3;
    for (let i = 0; i < count; i++) scene.time.delayedCall(i * 75, spawnBolt);
  };

  return { triggerBurst, updateFn: null };
}

// ── 🌿 Earth — Wind Sweep ─────────────────────────────────────────────────────
function _startEarth(scene, theme, W, H) {
  let windDir = 1;

  const scheduleWindChange = () => {
    scene.time.delayedCall(Phaser.Math.Between(8000, 15000), () => { windDir *= -1; scheduleWindChange(); });
  };
  scheduleWindChange();

  const LEAF_COLORS = [hexToPhaser(theme.primary), hexToPhaser(theme.secondary), 0x88cc44];

  const spawnLeaf = (gust = false) => {
    const startX  = windDir > 0 ? -12 : W + 12;
    const targetX = windDir > 0 ? W + 12 : -12;
    const y       = Phaser.Math.Between(H * 0.04, H * 0.86);
    const lw      = Phaser.Math.FloatBetween(4, 10);
    const lh      = Phaser.Math.FloatBetween(2, 5);
    const color   = LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)];
    const dur     = gust ? Phaser.Math.Between(500, 1100) : Phaser.Math.Between(1500, 4000);
    const p = scene.add.rectangle(startX, y, lw, lh, color, Phaser.Math.FloatBetween(0.5, 0.9))
      .setDepth(8).setAngle(Phaser.Math.Between(-60, 60));
    scene.tweens.add({
      targets: p, x: targetX, y: y + Phaser.Math.Between(-45, 45),
      angle: Phaser.Math.Between(-180, 180), alpha: 0, duration: dur,
      ease: 'Sine.InOut', onComplete: () => p.destroy(),
    });
  };

  scene.time.addEvent({ delay: 680, loop: true, callback: () => spawnLeaf() });

  const triggerBurst = ({ type = 'match', comboLevel = 1 } = {}) => {
    const count = comboLevel >= 3 ? 18 : 10;
    for (let i = 0; i < count; i++) scene.time.delayedCall(i * 28, () => spawnLeaf(true));
  };

  return { triggerBurst, updateFn: null };
}

// ── 🧠 Psychic — Orbital Vortex → Explosion ──────────────────────────────────
function _startPsychic(scene, theme, boardCX, boardCY, W, H) {
  const POOL         = 30;
  const orbiters     = [];
  let   comboAccel   = 0;
  let   exploding    = false;
  const primaryColor = hexToPhaser(theme.primary);
  const glowColor    = hexToPhaser(theme.glow);

  for (let i = 0; i < POOL; i++) {
    const angle  = (i / POOL) * Math.PI * 2;
    const radius = Phaser.Math.FloatBetween(80, 260);
    const size   = Phaser.Math.FloatBetween(2, 6);
    const color  = i % 3 === 0 ? glowColor : primaryColor;
    const p = scene.add.rectangle(
      boardCX + Math.cos(angle) * radius,
      boardCY + Math.sin(angle) * radius,
      size, size, color, Phaser.Math.FloatBetween(0.4, 0.9),
    ).setDepth(8).setBlendMode(Phaser.BlendModes.ADD);
    orbiters.push({ obj: p, angle, radius, speed: 0.006 + Math.random() * 0.009 });
  }

  const updateFn = () => {
    comboAccel *= 0.982;
    if (exploding) return;
    for (const o of orbiters) {
      o.angle += o.speed + comboAccel;
      o.obj.setPosition(
        boardCX + Math.cos(o.angle) * o.radius,
        boardCY + Math.sin(o.angle) * o.radius,
      );
    }
  };

  const triggerExplosion = () => {
    if (exploding) return;
    exploding = true;
    comboAccel = 0;
    for (const o of orbiters) {
      const dx  = o.obj.x - boardCX;
      const dy  = o.obj.y - boardCY;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      scene.tweens.add({
        targets: o.obj,
        x: o.obj.x + (dx / len) * 280,
        y: o.obj.y + (dy / len) * 280,
        alpha: 0, duration: 550, ease: 'Quad.Out',
        onComplete: () => {
          o.angle = Math.random() * Math.PI * 2;
          o.obj.setAlpha(Phaser.Math.FloatBetween(0.35, 0.8));
          o.obj.setPosition(boardCX + Math.cos(o.angle) * o.radius, boardCY + Math.sin(o.angle) * o.radius);
        },
      });
    }
    // No screen flash — particle burst is sufficient visual feedback
    scene.time.delayedCall(600, () => { exploding = false; });
  };

  const triggerBurst = ({ type = 'match', comboLevel = 1 } = {}) => {
    comboAccel += 0.005;
    if (comboLevel >= 3 || comboAccel > 0.045) triggerExplosion();
  };

  return { triggerBurst, updateFn };
}

// ─────────────────────────────────────────────────────────────────────────────
// 7c. Energy floor line — glowing ground beneath the board
// ─────────────────────────────────────────────────────────────────────────────
function _drawFloorLine(scene, theme, boardX, boardY, boardW, boardH) {
  const accent = hexToPhaser(theme.boardGlow);
  const lineY  = boardY + boardH + 2;

  const line = scene.add.graphics().setDepth(25);

  // Outer diffuse band
  line.fillStyle(accent, 0.13);
  line.fillRect(boardX - 8, lineY,     boardW + 16, 6);

  // Core bright line
  line.fillStyle(accent, 0.60);
  line.fillRect(boardX,     lineY + 2, boardW,      2);

  // Pulse — offset from the frame pulse so they breathe independently
  scene.tweens.add({
    targets:  line,
    alpha:    { from: 1.0, to: 0.20 },
    duration: 1700,
    yoyo:     true,
    repeat:   -1,
    ease:     'Sine.InOut',
    delay:    200,
  });
}
